import { useEffect, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';

type ContextRow = {
  resourceRef: string;
  owner: string | null;
  policyRef: string | null;
  classification: string | null;
  provenance: string;
};

const boundedText = (value: unknown) =>
  typeof value === 'string' && value.length <= 500 ? value : null;

/** Read-only prefill from the user's authorized Catalog view. The backend
 * independently resolves current approved policies before any collection. */
export function InheritedCatalogContext({
  resourceRefs,
}: {
  resourceRefs: string[];
}) {
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const selection = JSON.stringify(
    [
      ...new Set(
        resourceRefs.filter(
          ref =>
            typeof ref === 'string' &&
            /^resource:[a-z0-9._-]+\/[a-z0-9._-]+$/.test(ref),
        ),
      ),
    ]
      .slice(0, 200)
      .sort(),
  );
  const [rows, setRows] = useState<ContextRow[]>([]);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    setRows([]);
    setError('');
    const refs = JSON.parse(selection) as string[];
    if (!refs.length)
      return () => {
        active = false;
      };
    void discovery
      .getBaseUrl('catalog')
      .then(async base => {
        const response = await fetchApi.fetch(`${base}/entities/by-refs`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            entityRefs: refs,
            fields: ['kind', 'metadata', 'spec.owner'],
          }),
        });
        if (!response.ok)
          throw new Error(
            'Catalog context is unavailable. The backend still validates inherited requirements before collection.',
          );
        const value = await response.json();
        if (!Array.isArray(value?.items) || value.items.length !== refs.length)
          throw new Error(
            'Catalog context could not be matched to the selected resources.',
          );
        const context = refs.map((ref, index): ContextRow => {
          const entity = value.items[index];
          const metadata = entity?.metadata;
          const matched =
            entity &&
            typeof entity.kind === 'string' &&
            `${entity.kind.toLowerCase()}:${metadata?.namespace ?? 'default'}/${
              metadata?.name
            }` === ref;
          if (!matched)
            return {
              resourceRef: ref,
              owner: null,
              policyRef: null,
              classification: null,
              provenance:
                'Catalog mapping unavailable; no owner or policy declaration inferred.',
            };
          return {
            resourceRef: ref,
            owner: boundedText(entity.spec?.owner),
            policyRef: boundedText(
              metadata.annotations?.['horizon.io/assessment-policy-ref'],
            ),
            classification: boundedText(
              metadata.annotations?.['horizon.io/data-classification'],
            ),
            provenance: `Catalog ${ref}; UID ${
              boundedText(metadata.uid) ?? 'unavailable'
            }; revision ${
              boundedText(metadata.etag) ??
              boundedText(metadata.resourceVersion) ??
              'unavailable'
            }`,
          };
        });
        if (active) setRows(context);
      })
      .catch(caught => {
        if (active)
          setError(
            caught instanceof Error
              ? caught.message
              : 'Catalog context unavailable',
          );
      });
    return () => {
      active = false;
    };
  }, [selection, discovery, fetchApi]);
  if (!resourceRefs.length) return null;
  return (
    <Box mt={2}>
      <Typography variant="subtitle1">
        Inherited Catalog context — read-only
      </Typography>
      <Typography variant="body2">
        These mappings are not new owner declarations or collection approvals.
        Policy currency and scope are validated server-side. Leaving Business
        context blank does not erase inherited requirements.
      </Typography>
      {error && <Typography color="error">{error}</Typography>}
      {rows.map(row => (
        <Box key={row.resourceRef} mt={1}>
          <Typography>{row.resourceRef}</Typography>
          <Typography variant="body2">
            Technical owner mapping: {row.owner ?? 'Unavailable'} · Policy
            mapping: {row.policyRef ?? 'Not mapped'} · Catalog classification:{' '}
            {row.classification ?? 'Not declared'}
          </Typography>
          <Typography variant="caption">{row.provenance}</Typography>
        </Box>
      ))}
    </Box>
  );
}
